/**
 * AnswersTrailCard.tsx
 *
 * W26 — Visual Professionalism Overhaul
 * Fixed bare-key bug: getQuestionLabel and getOptionLabel now handle legacy
 * "cp{N}_q{M}" key format (from seed data / older records) by falling back
 * to a position-based lookup — cp number + ordinal index into ALL_QUESTIONS
 * for that CP. The API returns whatever key is stored in account_answers;
 * this client-side layer bridges both key formats.
 *
 * W19.1 — Flowcharts: Questions-answered-to-reach-CP trail card per account context.
 *
 * Shows the actual questions and locked answers for a given account at a selected CP.
 * Collapsible, with localStorage persistence keyed by cp_number.
 *
 * Palette: gray #1F2937 · red #DC2626 · orange #CA8A04 · yellow #CA8A04 · green #059669
 * No emojis. No exclamation points. M-PACT hyphenated. Tenant 1 only. CANON_VISUALS_LOCKED.
 */

import { useEffect, useState } from "react";
import { api } from "@/lib/session";
import { ALL_QUESTIONS } from "@shared/questions";
import { ChevronDown, ChevronRight } from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────────

interface CpProgressAnswer {
  question_key: string;
  selected_option: string | null;
  updated_at: string | null;
  evidence_type: "text" | "url" | "file" | null;
}

interface CpProgressEntry {
  cp_number: number;
  status: "green" | "amber" | "red" | null;
  answered: number;
  total: number;
  answers: CpProgressAnswer[];
}

interface CpProgress {
  account_id: number;
  composite_score: number;
  cps: CpProgressEntry[];
}

// ─── Key resolution helpers ──────────────────────────────────────────────────

/**
 * Resolve a question key to its QuestionDef from ALL_QUESTIONS.
 *
 * Two cases handled:
 *   1. Modern key: "cp1_project_exists" — direct lookup by q.key === key
 *   2. Legacy key: "cp1_q1" — extract CP number + ordinal, then return the
 *      Nth question for that CP in ALL_QUESTIONS order (1-indexed).
 *
 * Returns undefined if no match found.
 */
function resolveQuestion(key: string) {
  // 1. Direct match (modern keys)
  const direct = ALL_QUESTIONS.find((q) => q.key === key);
  if (direct) return direct;

  // 2. Legacy pattern: cp{N}_q{M}
  const legacyMatch = key.match(/^cp(\d+)_q(\d+)$/i);
  if (legacyMatch) {
    const cpNum = parseInt(legacyMatch[1], 10);
    const ordinal = parseInt(legacyMatch[2], 10); // 1-indexed
    const cpQuestions = ALL_QUESTIONS.filter((q) => q.cp === cpNum);
    const resolved = cpQuestions[ordinal - 1]; // ordinal is 1-based
    if (resolved) return resolved;
  }

  return undefined;
}

function getQuestionLabel(key: string): string {
  const q = resolveQuestion(key);
  if (q) return q.label;
  // Last-resort: format the raw key readably
  return key.replace(/_/g, " ").replace(/^cp(\d+) /, "CP$1: ");
}

function getOptionLabel(questionKey: string, optionKey: string | null): string {
  if (!optionKey) return "—";
  const q = resolveQuestion(questionKey);
  if (!q) return optionKey;
  const opt = q.options.find((o) => o.key === optionKey);
  return opt?.label ?? optionKey;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

const EVIDENCE_CHIP: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  url:  { label: "URL",  bg: "#EFF6FF", text: "#0B0B0F" },
  text: { label: "Text", bg: "#F0FDF4", text: "#166534" },
  file: { label: "File", bg: "#FFF7ED", text: "#9A3412" },
};

// ─── Component ───────────────────────────────────────────────────────────────

interface AnswersTrailCardProps {
  accountId: string;
  accountName: string | null;
  selectedCp: number;
}

export default function AnswersTrailCard({
  accountId,
  accountName,
  selectedCp,
}: AnswersTrailCardProps) {
  const lsKey = `aip:flowcharts:answers-collapsed:${selectedCp}`;

  const [cpEntry, setCpEntry] = useState<CpProgressEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // collapsed is null until we know whether there are answers
  const [collapsed, setCollapsed] = useState<boolean | null>(null);

  // Fetch cp-progress for this account
  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    setCpEntry(null);
    setCollapsed(null);

    api<CpProgress>(`/api/accounts/${accountId}/cp-progress`)
      .then((progress) => {
        const entry = progress.cps.find((c) => c.cp_number === selectedCp) ?? null;
        setCpEntry(entry);

        // Determine default collapsed state
        const stored = localStorage.getItem(lsKey);
        if (stored !== null) {
          setCollapsed(stored === "true");
        } else {
          // Default expanded when CP has answers; collapsed otherwise
          setCollapsed(!(entry && entry.answers.length > 0));
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Unable to load CP progress. Please try again.");
        setLoading(false);
      });
  }, [accountId, selectedCp]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(lsKey, String(next));
      return next;
    });
  }

  // ─── Empty state: no account ────────────────────────────────────────────
  // (This component only renders when accountId is present, so this branch
  //  is defensive; the parent handles the true "no account" empty state.)

  const title = accountName
    ? `Questions answered to reach CP${selectedCp} — ${accountName}`
    : `Questions answered to reach CP${selectedCp}`;

  return (
    <div
      className="rounded-lg border bg-white shadow-sm overflow-hidden"
      style={{ borderColor: "#E5E7EB" }}
    >
      {/* ── Card header — always visible ─────────────────────────────── */}
      <button
        onClick={toggleCollapsed}
        className="w-full flex items-center justify-between px-5 py-3 border-b text-left transition-colors hover:bg-[#F9FAFB]"
        style={{ borderColor: "#E5E7EB", background: "#FAFAF9" }}
        aria-expanded={!collapsed}
        aria-controls="answers-trail-body"
      >
        <h3 className="text-[14px] font-semibold tracking-tight text-[#0B0B0F]">
          {title}
        </h3>
        <span className="ml-3 flex-shrink-0 text-[#6B7280]">
          {collapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden />
          )}
        </span>
      </button>

      {/* ── Card body ────────────────────────────────────────────────── */}
      {!collapsed && (
        <div id="answers-trail-body" className="px-5 py-4">
          {loading && (
            <p className="text-[13px] text-[#6B7280]">
              Loading answers for CP{selectedCp}...
            </p>
          )}

          {!loading && error && (
            <p className="text-[13px] text-[#DC2626]">{error}</p>
          )}

          {!loading && !error && cpEntry && cpEntry.answers.length === 0 && (
            <p className="text-[13px] text-[#6B7280]">
              No answers locked yet at CP{selectedCp} for this account.
            </p>
          )}

          {!loading && !error && cpEntry && cpEntry.answers.length > 0 && (
            <ol className="space-y-4">
              {cpEntry.answers.map((answer, idx) => {
                const questionLabel = getQuestionLabel(answer.question_key);
                const optionLabel = getOptionLabel(
                  answer.question_key,
                  answer.selected_option,
                );
                const chip = answer.evidence_type
                  ? EVIDENCE_CHIP[answer.evidence_type] ?? null
                  : null;

                return (
                  <li
                    key={answer.question_key}
                    className="rounded-md border px-4 py-3"
                    style={{ borderColor: "#E5E7EB", background: "#FAFAFA" }}
                  >
                    {/* Question label */}
                    <p className="text-[13px] font-semibold text-[#0B0B0F] leading-snug">
                      <span className="text-[#9CA3AF] font-normal mr-1.5">
                        {idx + 1}.
                      </span>
                      {questionLabel}
                    </p>

                    {/* Selected option */}
                    <p className="mt-1.5 text-[13px] text-[#374151] leading-snug">
                      {optionLabel}
                    </p>

                    {/* Metadata row */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {/* Timestamp */}
                      <span
                        className="text-[11px] text-[#9CA3AF]"
                        style={{ fontFamily: "ui-monospace, monospace" }}
                      >
                        Locked {formatTimestamp(answer.updated_at)}
                      </span>

                      {/* Evidence chip */}
                      {chip && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{
                            background: chip.bg,
                            color: chip.text,
                          }}
                        >
                          {chip.label}
                        </span>
                      )}
                      {!chip && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{
                            background: "#F3F4F6",
                            color: "#6B7280",
                          }}
                        >
                          None
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
